// src/services/ApiClient.js
import CryptoJS from 'crypto-js';
import pako from 'pako';

/**
 * Processes API path by removing asterisks and replacing path variables with values from params
 * @param {string} path - The API path with variables and asterisks
 * @param {object} params - Parameters object containing values for replacement
 * @returns {object} Object with processed path and updated params
 */
function processApiPath(path, params) {
  console.log("Processing API path:");
  console.log("- Original path:", path);
  console.log("- Original params:", JSON.stringify(params, null, 2));
  
  // 1. Remove all asterisks from path
  let processedPath = path.replace(/\*/g, '');
  console.log("- Path after removing asterisks:", processedPath);
  
  // 2. Create a copy of params to modify
  const updatedParams = { ...params };
  
  // 3. Find and replace all variables in the path {variable}
  const pathVarPattern = /\{([^}]+)\}/g;
  let match;
  let variablesReplaced = false;
  
  // Using exec to iterate through all matches
  while ((match = pathVarPattern.exec(path)) !== null) {
    const fullMatch = match[0]; // {resource_id}
    const varName = match[1];   // resource_id
    
    if (updatedParams[varName] !== undefined) {
      // Replace the variable in the path
      processedPath = processedPath.replace(fullMatch, updatedParams[varName]);
      console.log(`- Replaced ${fullMatch} with "${updatedParams[varName]}"`);
      
      // Remove the used key from params
      delete updatedParams[varName];
      console.log(`- Removed "${varName}" from params object`);
      variablesReplaced = true;
    } else {
      console.warn(`- Warning: No value found for path variable "${varName}"`);
    }
  }
  
  if (!variablesReplaced) {
    console.log("- No variables found or replaced in path");
  }
  
  console.log("- Final processed path:", processedPath);
  console.log("- Final params:", Object.keys(updatedParams).length, "parameters remaining");
  
  return {
    processedPath,
    updatedParams
  };
}

/**
 * Utility for making API calls with different authentication methods
 */
export default class ApiClient {
  /**
   * Create a new API client
   * @param {Object} credentials - User credentials (userId, apiKey)
   * @param {Object} options - Configuration options
   */
  constructor(credentials, options = {}) {
    this.credentials = credentials;
    this.timeout = options.timeout || 30000;
    this.callbacks = options.callbacks || {};
    // this.abortController = new AbortController();
    this.abortController = options.abortController || new AbortController();
    
    // Store whether we own the abort controller (created it internally)
    this._ownsAbortController = !options.abortController;
  }

  /**
   * Execute an API call based on endpoint configuration
   * @param {Object} endpoint - The endpoint configuration
   * @param {Object} customParams - Optional custom parameters to override endpoint params
   * @returns {Promise<Object>} - The API response
   */
  async executeApiCall(endpoint, customParams = {}) {
    try {
      console.log("executeApiCall - endpoint: ", endpoint);
      console.log("executeApiCall - customParams: ", customParams);
      
      const { path, method, root_url } = endpoint;
      const params = { ...endpoint.params, ...customParams };
      const { userId, apiKey } = this.credentials;
  
      // Determine if this is a V2 API call
      const isV2Api = root_url.includes('v2') || path.includes('/api2/');
      
      // Use the unified function for both API versions
      return this.executeUnifiedApiCall(
        root_url, 
        path, 
        method, 
        userId, 
        apiKey, 
        params, 
        isV2Api
      );
    } catch (error) {
      console.error("executeApiCall failed:", error);
      
      // Notify callback on error
      this._notifyCallback('onError', { 
        endpoint, 
        error,
        phase: 'preparation'
      });
      
      // Ensure error is properly propagated to caller
      throw error;
    }
  }
  
  /**
   * Execute an API call to any API version with unified error handling
   * @param {string} rootUrl - The base URL for the API
   * @param {string} path - The endpoint path
   * @param {string} method - HTTP method (GET, POST, etc.)
   * @param {string} userId - User ID for authentication
   * @param {string} apiKey - API key for authentication
   * @param {Object} params - Request parameters
   * @param {boolean} isV2Api - Whether this is a V2 API call
   * @returns {Promise<Object>} - The API response
   */
  async executeUnifiedApiCall(rootUrl, path, method, userId, apiKey, params, isV2Api) {
    try {
      // Process the path and update params
      const { processedPath, updatedParams } = processApiPath(path, params);

      // // 1. Import required libraries
      // const pako = await import('./pako');
      // const CryptoJS = await import('./crypto-js');
      
      // 2. Properly construct the URL
      // const fullUrl = rootUrl + path;
      const fullUrl = rootUrl + processedPath;
      
      let url;
      try {
        url = new URL(fullUrl);
      } catch (error) {
        console.error(`Invalid URL construction: ${fullUrl}`, error);
        throw new Error(`Failed to construct URL: ${error.message}`);
      }
      
      // 3. Determine if we should use a request body based on method
      const methodsWithBody = ['POST', 'PUT', 'PATCH'];
      const shouldUseBody = methodsWithBody.includes(method.toUpperCase()) && !isV2Api;
      
      // 4. Create message with auth info and parameters based on API version and method
      let msgToSign;
      
      if (isV2Api) {
        // For V2 APIs, include all params in the auth message
        msgToSign = {
          _owner: userId,
          _timestamp: Math.floor(Date.now() / 1000),
          ...params
        };
      } else {
        // For standard APIs, create auth message
        const authMsg = {
          _owner: userId,
          _timestamp: Math.floor(Date.now() / 1000)
        };
        
        // For GET requests, include all params in the auth message
        if (!shouldUseBody) {
          // Object.assign(authMsg, params);
          Object.assign(authMsg, updatedParams);
        }
        
        msgToSign = authMsg;
      }
      
      // 5. Compress and sign the message
      try {
        const msgString = JSON.stringify(msgToSign);
        const deflated = pako.deflate(msgString, { level: 9 });
        const base64EncodedMsg = btoa(String.fromCharCode.apply(null, deflated)).trim();
        const signature = CryptoJS.HmacSHA256(base64EncodedMsg, apiKey).toString(CryptoJS.enc.Hex);
        
        // 6. Add authentication parameters to URL
        url.searchParams.append('msg', base64EncodedMsg);
        url.searchParams.append('sig', signature);
      } catch (error) {
        console.error('Error during message preparation:', error);
        throw new Error(`Authentication preparation failed: ${error.message}`);
      }
      
      // 7. Prepare fetch options with headers based on API version
      const options = {
        method,
        signal: this.abortController.signal,
        headers: {}
      };
      
      // 8. Add request body for appropriate methods (not for V2 APIs)
      // if (shouldUseBody && Object.keys(params).length > 0) {
      if (shouldUseBody && Object.keys(updatedParams).length > 0) {
        try {
          // options.body = JSON.stringify(params);
          options.body = JSON.stringify(updatedParams);
          
          // Only add Content-Type header for standard API, NOT for v2 APIs
          if (!isV2Api) {
            options.headers['Content-Type'] = 'application/json';
          }
        } catch (error) {
          console.error('Error preparing request body:', error);
          throw new Error(`Request body preparation failed: ${error.message}`);
        }
      }
      
      console.log(`API Call: ${method} ${url.toString()}`, 
        shouldUseBody ? `Body: ${options.body}` : 'No Body',
        `API Version: ${isV2Api ? 'V2' : 'Standard'}`);
      
      // 9. Make the request with proper error handling
      return this._makeRequest(url, options);
      
    } catch (error) {
      // 10. Comprehensive error handling for the entire function
      console.error('API call preparation failed:', error);
      
      // Notify callback on error
      this._notifyCallback('onError', { 
        url: rootUrl + path, 
        error: error,
        phase: 'preparation',
        apiVersion: isV2Api ? 'v2' : 'standard'
      });
      
      // Rethrow with more context
      throw new Error(`API call failed during preparation: ${error.message}`);
    }
  }

  /**
   * Common request handling with timeout
   */
  async _makeRequest(url, options) {
    // Notify callback before request if available
    this._notifyCallback('beforeRequest', { 
      url: url.toString(), 
      options, 
      startTime: new Date() 
    });
    
    // Add timeout promise race
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        console.log('Timeout reached for request');
        reject(new Error('Request timeout'));
      }, this.timeout);
    });
    
    try {
      console.log("Making API call to:", url.toString());
      console.log("With options:", JSON.stringify(options));
      
      const fetchPromise = fetch(url.toString(), options);
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      console.log("API response received:", response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log("API response data:", responseData);
      
      // Prepare response object with status
      const responseWithStatus = {
        data: responseData,
        status: 'success'
      };
      
      // Notify callback after response if available
      this._notifyCallback('afterResponse', { 
        url: url.toString(), 
        response: responseWithStatus,
        status: 'success'
      });
      
      // Return response with status for consistency with original implementation
      return {
        data: responseData,
        status: 'success'
      };

    } catch (error) {
      // Notify callback on error if available
      this._notifyCallback('onError', { 
        url: url.toString(), 
        error,
        phase: 'request'  // Indicate this failed during the actual request
      });
      
      if (error.name === 'AbortError') {
        throw new Error('Request was interrupted');
      }
      throw error;
    }
  }
  
  /**
   * Helper method for callbacks
   * @private
   */
  _notifyCallback(event, data) {
    if (this.callbacks && this.callbacks[event]) {
      this.callbacks[event](data);
    }
  }
  
  /**
   * Interrupt ongoing API call
   */
  interrupt() {
    this.abortController.abort();
  }
  // interrupt() {
  //   if (this._ownsAbortController) {
  //     this.abortController.abort();
  // }
}

/**
 * Simple function to make a one-off API call without creating a client instance
 * @param {Object} endpoint - The endpoint configuration
 * @param {Object} credentials - User credentials
 * @param {Object} options - Optional configuration
 * @param {Object} customParams - Optional params to override endpoint params
 * @returns {Promise<Object>} - The API response
 */
export async function makeApiCall(endpoint, credentials, options = {}, customParams = {}) {
  const client = new ApiClient(credentials, options);
  try {
    return await client.executeApiCall(endpoint, customParams);
  } finally {
    // Ensure proper cleanup even if the call fails
    client.interrupt();
  }
}


/**
 * Poll the Cloud Slicer job status until it reaches a target state or times out
 * @param {string} jobId - The Cloud Slicer job ID to poll
 * @param {string|Array<string>} targetStates - The state(s) to wait for (e.g., 'complete', ['complete', 'error'])
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - The final API response when condition is met
 */
// export const trackCloudSlicerJobStatus = async (jobId, targetStates, options = {}) => {
//   const {
//     maxAttempts = 100,           // Maximum polling attempts
//     intervalMs = 5000,          // Polling interval in milliseconds
//     credentials,                // User credentials (required)
//     onProgress = null,          // Optional callback for progress updates
//     timeoutMs = 10 * 60 * 1000  // Overall timeout (10 minutes by default)
//   } = options;
  
//   // Normalize targetStates to array
//   const targetStateArray = Array.isArray(targetStates) ? targetStates : [targetStates];
  
//   // Create API client
//   const client = new ApiClient(credentials, {
//     timeout: 30000, // 30 seconds timeout for each individual request
//     callbacks: {
//       afterResponse: (data) => {
//         if (onProgress) {
//           onProgress({
//             response: data.response,
//             attempt: attempts,
//             maxAttempts
//           });
//         }
//       }
//     }
//   });
  
//   // Configure the endpoint
//   const endpoint = {
//     path: '/api2/cloudslicer/jobs/get',
//     method: 'GET',
//     root_url: 'https://services.uplynk.com',
//     params: {
//       id: jobId
//     }
//   };
  
//   // Start polling
//   let attempts = 0;
//   const startTime = Date.now();
  
//   while (attempts < maxAttempts) {
//     attempts++;
    
//     // Check for overall timeout
//     if (Date.now() - startTime > timeoutMs) {
//       throw new Error(`Polling timed out after ${timeoutMs}ms`);
//     }
    
//     try {
//       // Make the API call
//       const response = await client.executeApiCall(endpoint);
//       console.log(`Poll attempt ${attempts}/${maxAttempts}:`, response);
      
//       // Extract the job status from response
//       const jobStatus = response?.data?.job?.state || 'unknown';
      
//       // Check if we've reached the target state
//       if (targetStateArray.includes(jobStatus)) {
//         console.log(`Job reached target state: ${jobStatus}`);
//         return response;
//       }
      
//       // If job failed with an error state that's not in our target states
//       if (jobStatus === 'error' && !targetStateArray.includes('error')) {
//         throw new Error(`Job failed with status: ${jobStatus}`);
//       }
      
//       // Wait before next poll
//       await new Promise(resolve => setTimeout(resolve, intervalMs));
      
//     } catch (error) {
//       console.error(`Poll attempt ${attempts} failed:`, error);
      
//       // For network errors, we'll continue trying
//       if (attempts < maxAttempts) {
//         // Exponential backoff - increase wait time for network errors
//         await new Promise(resolve => setTimeout(resolve, intervalMs * (1 + attempts * 0.5)));
//         continue;
//       }
      
//       throw error;
//     }
//   }
  
//   throw new Error(`Polling exceeded maximum attempts (${maxAttempts})`);
// }

export const trackCloudSlicerJobStatus = async (jobId, targetStates, options = {}) => {
  const {
    maxAttempts = 100,           // Maximum polling attempts
    intervalMs = 5000,          // Polling interval in milliseconds
    credentials,                // User credentials (required)
    onProgress = null,          // Optional callback for progress updates
    timeoutMs = 10 * 60 * 1000,  // Overall timeout (10 minutes by default)
    onComplete,
    abortController = new AbortController() // Accept external abort controller
  } = options;
  
  // Normalize targetStates to array
  const targetStateArray = Array.isArray(targetStates) ? targetStates : [targetStates];
  
  // Create API client with the shared abort controller
  const client = new ApiClient(credentials, {
    timeout: 30000, // 30 seconds timeout for each individual request
    abortController: abortController, // Pass the shared abort controller
    callbacks: {
      afterResponse: (data) => {
        if (onProgress) {
          onProgress({
            response: data.response,
            attempt: attempts,
            maxAttempts
          });
        }
      }
    }
  });
  
  // Configure the endpoint
  const endpoint = {
    path: '/api2/cloudslicer/jobs/get',
    method: 'GET',
    root_url: 'https://services.uplynk.com',
    params: {
      id: jobId
    }
  };
  
  // Start polling
  let attempts = 0;
  const startTime = Date.now();
  
  while (attempts < maxAttempts) {
    // Check if aborted
    if (abortController.signal.aborted) {
      throw new Error('Job tracking was aborted');
    }
    
    attempts++;
    
    // Check for overall timeout
    if (Date.now() - startTime > timeoutMs) {
      throw new Error(`Polling timed out after ${timeoutMs}ms`);
    }
    
    try {
      // Make the API call
      const response = await client.executeApiCall(endpoint);
      console.log(`Poll attempt ${attempts}/${maxAttempts}:`, response);
      
      // Extract the job status from response
      const jobStatus = response?.data?.job?.state || 'unknown';
      
      // Check if we've reached the target state
      if (targetStateArray.includes(jobStatus)) {
        console.log(`Job reached target state: ${jobStatus}`);
        if (onComplete) {
          onComplete(response);
        }
        return response;
      }
      
      // If job failed with an error state that's not in our target states
      if (jobStatus === 'error' && !targetStateArray.includes('error')) {
        throw new Error(`Job failed with status: ${jobStatus}`);
      }
      
      // Check if aborted before waiting
      if (abortController.signal.aborted) {
        throw new Error('Job tracking was aborted');
      }
      
      // Wait before next poll - FIXED VERSION
      await new Promise((resolve, reject) => {
        // Create timeout for interval
        const timeoutId = setTimeout(() => {
          // Remove the event listener when the timeout completes
          abortController.signal.removeEventListener('abort', abortHandler);
          resolve();
        }, intervalMs);
        
        // Listen for abort signal during wait period
        const abortHandler = () => {
          clearTimeout(timeoutId);
          reject(new Error('Job tracking was aborted'));
        };
        
        // Add abort listener for this wait period
        abortController.signal.addEventListener('abort', abortHandler);
      });
      
    } catch (error) {
      // Check if this was an abort error
      if (abortController.signal.aborted || error.name === 'AbortError' || 
          error.message === 'Job tracking was aborted') {
        throw new Error('Job tracking was aborted');
      }
      
      console.error(`Poll attempt ${attempts} failed:`, error);
      
      // For network errors, we'll continue trying
      if (attempts < maxAttempts) {
        // Check if aborted before waiting
        if (abortController.signal.aborted) {
          throw new Error('Job tracking was aborted');
        }
        
        // Exponential backoff - increase wait time for network errors - FIXED VERSION
        await new Promise((resolve, reject) => {
          const backoffTime = intervalMs * (1 + attempts * 0.5);
          
          // Create timeout with cleanup in the callback
          const timeoutId = setTimeout(() => {
            // Remove the event listener when the timeout completes
            abortController.signal.removeEventListener('abort', abortHandler);
            resolve();
          }, backoffTime);
          
          // Listen for abort signal during backoff period
          const abortHandler = () => {
            clearTimeout(timeoutId);
            reject(new Error('Job tracking was aborted'));
          };
          
          // Add abort listener
          abortController.signal.addEventListener('abort', abortHandler);
        });
        
        continue;
      }
      
      throw error;
    }
  }
  
  throw new Error(`Polling exceeded maximum attempts (${maxAttempts})`);
}